import {
  cancelInvite as generatedCancelInvite,
  createInvite as generatedCreateInvite,
  getGetInviteCreditSummariesUrl,
  getGetInviteDashboardUrl,
  getInviteCreditSummaries as generatedGetInviteCreditSummaries,
  getInviteDashboard as generatedGetInviteDashboard,
  grantInviteCredits as generatedGrantInviteCredits,
  requestInviteCredits as generatedRequestInviteCredits,
} from './api/generated';
import type {
  CreateInviteRequest,
  CreateInviteResponse,
  InviteCreditSummaryResponse,
  InviteDashboardResponse,
  PendingInviteResponse,
} from './api/generated';
import { API_URL, FRONTEND_URL } from './consts';

export const ADMIN_INVITE_CREDITS_URL = `${API_URL}${getGetInviteCreditSummariesUrl()}`;
export const INVITE_DASHBOARD_URL = `${API_URL}${getGetInviteDashboardUrl()}`;

/** Application-facing alias for the generated pending-invite contract. */
export type PendingInvite = PendingInviteResponse;

/** Application-facing alias for the generated invite dashboard contract. */
export type InviteDashboard = InviteDashboardResponse;

export type { CreateInviteRequest, CreateInviteResponse };

/** Application-facing alias for the generated admin credit summary. */
export type InviteCreditSummary = InviteCreditSummaryResponse;

export function buildInviteUrl(code: string): string {
  const path = `/register/${encodeURIComponent(code)}`;
  if (FRONTEND_URL) return new URL(path, FRONTEND_URL).toString();
  if (typeof window !== 'undefined' && /^https?:\/\//.test(window.location.origin)) {
    return new URL(path, window.location.origin).toString();
  }
  return path;
}

/** Copies an invitation URL, including in browsers where the asynchronous Clipboard API fails. */
export async function copyInviteUrl(code: string): Promise<void> {
  const inviteUrl = buildInviteUrl(code);
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      return;
    } catch {
      // The Clipboard API can be present but unavailable because of browser permissions.
    }
  }

  const textArea = document.createElement('textarea');
  const previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  textArea.value = inviteUrl;
  textArea.setAttribute('readonly', '');
  textArea.setAttribute('aria-hidden', 'true');
  textArea.tabIndex = -1;
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.append(textArea);
  textArea.select();
  try {
    if (!document.execCommand('copy')) throw new Error('Browser rejected clipboard copy');
  } finally {
    textArea.remove();
    previouslyFocused?.focus();
  }
}

export async function getInviteDashboard(): Promise<InviteDashboard> {
  return generatedGetInviteDashboard();
}

export async function createInvite(request: CreateInviteRequest): Promise<CreateInviteResponse> {
  return generatedCreateInvite(request);
}

export async function cancelInvite(inviteId: number): Promise<void> {
  await generatedCancelInvite({ invite_id: inviteId });
}

export async function requestInviteCredits(): Promise<void> {
  await generatedRequestInviteCredits();
}

export async function getInviteCreditSummaries(): Promise<InviteCreditSummary[]> {
  return generatedGetInviteCreditSummaries();
}

export async function grantInviteCredits(username: string, quantity: number): Promise<void> {
  await generatedGrantInviteCredits(username, { quantity });
}
